import {
  Chip,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listOrderShops, searchShopOrders } from '../../api/business/endpoints';
import type { OrderSortKey, OrderStatus } from '../../api/business/types';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { formatDateTime, formatMoney } from '../../components/format';
import { ServerPagination } from '../../components/ServerPagination';
import { SortableCell } from '../../components/SortableCell';
import { TableFilters } from '../../components/TableFilters';
import { TableSkeletonRows } from '../../components/TableSkeletonRows';
import { useDebouncedValue } from '../../components/useDebouncedValue';
import { useSort } from '../../components/useSort';
import { useLanguage } from '../../i18n/LanguageContext';

import { OrderStatusChip } from './OrderStatusChip';

const STATUSES: Array<OrderStatus> = [
  'NEW',
  'PAID',
  'PROCESSING',
  'SHIPPED',
  'DELIVERED',
  'CANCELLED',
  'RETURNED',
  'OTHER',
];

/** A day of the date filter as the instant it starts, here — `to` is the start of the day after it. */
const dayStart = (day: string, plusDays = 0): string | undefined => {
  if (!day) {
    return undefined;
  }

  const date = new Date(`${day}T00:00:00`);

  date.setDate(date.getDate() + plusDays);

  return date.toISOString();
};

/**
 * The orders of a company's linked shops (R0034) — on the Orders page across every shop, on a shop link's page
 * only its own (`linkId`), on a product's page those it was in (`productId`). Server-paginated and sorted.
 */
export const ShopOrdersTable = ({
  companyId,
  linkId,
  productId,
}: {
  companyId: string;
  linkId?: string;
  productId?: string;
}) => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [shop, setShop] = useState('');
  const [status, setStatus] = useState<OrderStatus | ''>('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [term, setTerm] = useState('');
  const debouncedTerm = useDebouncedValue(term);
  const [page, setPage] = useState(0);
  const sort = useSort<OrderSortKey>('placedAt', 'desc', () => setPage(0));
  const showShop = linkId === undefined;

  const shops = useQuery({
    queryKey: ['order-shops', companyId],
    queryFn: () => listOrderShops(companyId),
    enabled: showShop && companyId !== '',
  });
  const query = useQuery({
    queryKey: [
      'orders',
      companyId,
      linkId ?? shop,
      productId,
      status,
      from,
      to,
      debouncedTerm,
      page,
      sort.key,
      sort.direction,
    ],
    enabled: companyId !== '',
    queryFn: () =>
      searchShopOrders(companyId, {
        linkId: linkId ?? (shop || undefined),
        productId,
        status: status || undefined,
        placedFrom: dayStart(from),
        placedTo: dayStart(to, 1),
        search: debouncedTerm.trim() || undefined,
        page: page + 1,
        sort: sort.key,
        direction: sort.direction,
      }),
    placeholderData: keepPreviousData,
  });
  const rows = query.data?.items ?? [];
  const columns = showShop ? 6 : 5;

  return (
    <Paper variant="outlined">
      <ErrorNotice error={query.error ?? shops.error} onRetry={() => void query.refetch()} />
      <TableFilters>
        <TextField
          size="small"
          label={t('orders.search')}
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setPage(0);
          }}
          sx={{ minWidth: 240 }}
        />
        {showShop ? (
          <TextField
            select
            size="small"
            label={t('orders.shop')}
            value={shop}
            onChange={(event) => {
              setShop(event.target.value);
              setPage(0);
            }}
            sx={{ minWidth: 200 }}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            {(shops.data ?? []).map((one) => (
              <MenuItem key={one.linkId} value={one.linkId}>
                {one.name} · {t(`platform.${one.platform}` as const)}
              </MenuItem>
            ))}
          </TextField>
        ) : null}
        <TextField
          select
          size="small"
          label={t('orders.status')}
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as OrderStatus | '');
            setPage(0);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value="">{t('common.all')}</MenuItem>
          {STATUSES.map((one) => (
            <MenuItem key={one} value={one}>
              {t(`orderStatus.${one}` as const)}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          type="date"
          label={t('orders.from')}
          value={from}
          onChange={(event) => {
            setFrom(event.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          size="small"
          type="date"
          label={t('orders.to')}
          value={to}
          onChange={(event) => {
            setTo(event.target.value);
            setPage(0);
          }}
          InputLabelProps={{ shrink: true }}
        />
      </TableFilters>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              {showShop ? <TableCell>{t('orders.shop')}</TableCell> : null}
              <SortableCell sort={sort} column="reference">
                {t('orders.reference')}
              </SortableCell>
              <SortableCell sort={sort} column="placedAt">
                {t('orders.placedAt')}
              </SortableCell>
              <SortableCell sort={sort} column="customerName">
                {t('orders.customer')}
              </SortableCell>
              <SortableCell sort={sort} column="status">
                {t('orders.status')}
              </SortableCell>
              <SortableCell sort={sort} column="totalGrossMinor" align="right">
                {t('orders.total')}
              </SortableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {query.isLoading ? (
              <TableSkeletonRows columns={columns} />
            ) : (
              rows.map((order) => (
                <TableRow
                  key={order.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/orders/${companyId}/${order.id}`)}
                >
                  {showShop ? (
                    <TableCell>
                      {order.linkName}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {t(`platform.${order.platform}` as const)}
                      </Typography>
                    </TableCell>
                  ) : null}
                  <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13 }}>
                    {order.reference}
                    {order.fromHistory ? (
                      <Tooltip title={t('orders.pastHint')}>
                        <Chip size="small" label={t('orders.past')} sx={{ ml: 1 }} />
                      </Tooltip>
                    ) : null}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(order.placedAt, language)}
                  </TableCell>
                  <TableCell>
                    {order.customerName ?? t('common.none')}
                    {order.customerEmail ? (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {order.customerEmail}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <OrderStatusChip status={order.status} label={order.statusLabel} />
                  </TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    {formatMoney(order.totalGrossMinor, order.currency, language)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {!query.isLoading && rows.length === 0 ? (
        <EmptyState title={t('orders.empty')} detail={t('orders.emptyDetail')} />
      ) : null}
      <ServerPagination
        page={page}
        pageSize={query.data?.pageSize ?? 25}
        total={query.data?.total ?? 0}
        onPageChange={setPage}
      />
    </Paper>
  );
};
